import { supabase } from './supabase'

export type AnnouncementPriority = 'info' | 'alerta' | 'critico'
export type AnnouncementScope = 'all' | 'presencial' | 'call-center' | 'unit'
export type AnnouncementFollowupStatus = 'nao_visualizado' | 'visualizado' | 'ciente'

export type Announcement = {
  id: number
  title: string
  body: string
  reference_code: string | null
  priority: AnnouncementPriority
  target_scope: AnnouncementScope
  target_unit_key: string | null
  require_ack: boolean
  pinned: boolean
  starts_at: string
  expires_at: string | null
  first_seen_at: string | null
  acknowledged_at: string | null
  unread: boolean
  pending_ack: boolean
}

export type AnnouncementManagementRow = {
  id: number
  title: string
  body: string
  reference_code: string | null
  priority: AnnouncementPriority
  target_scope: AnnouncementScope
  target_unit_key: string | null
  require_ack: boolean
  pinned: boolean
  starts_at: string
  expires_at: string | null
  status: 'ativo' | 'encerrado'
  created_at: string
  closed_at: string | null
  report_generated_at: string | null
  delete_after: string | null
  recipients: number
  viewed: number
  acknowledged: number
  viewed_pct: number
  acknowledged_pct: number
  avg_ack_minutes: number | null
}

export type AnnouncementFollowupRecipient = {
  user_id: string
  nome: string
  email: string
  unit_key: string
  unit_label: string
  status: AnnouncementFollowupStatus
  first_seen_at: string | null
  acknowledged_at: string | null
}

export async function loadMyAnnouncements(): Promise<Announcement[]> {
  const { data, error } = await supabase.rpc('list_my_announcements')
  if (error) throw error
  return (data ?? []) as Announcement[]
}

export async function markAnnouncementSeen(id: number) {
  const { error } = await supabase.rpc('mark_announcement_seen', { p_announcement_id: id })
  if (error) throw error
}

export async function acknowledgeAnnouncement(id: number) {
  const { error } = await supabase.rpc('acknowledge_announcement', { p_announcement_id: id })
  if (error) throw error
}

export async function createAnnouncement(input: {
  title: string
  body: string
  referenceCode?: string
  priority: AnnouncementPriority
  targetScope: AnnouncementScope
  targetUnitKey?: string | null
  requireAck: boolean
  pinned: boolean
  expiresAt?: string | null
}) {
  const { data, error } = await supabase.rpc('create_announcement', {
    p_title: input.title,
    p_body: input.body,
    p_reference_code: input.referenceCode ?? '',
    p_priority: input.priority,
    p_target_scope: input.targetScope,
    p_target_unit_key: input.targetUnitKey ?? null,
    p_require_ack: input.requireAck,
    p_pinned: input.pinned,
    p_expires_at: input.expiresAt || null,
  })
  if (error) throw error
  return data as number
}

export async function closeAnnouncement(id: number) {
  const { error } = await supabase.rpc('close_announcement', { p_announcement_id: id })
  if (error) throw error
}

export async function markAnnouncementReportGenerated(id: number) {
  const { data, error } = await supabase.rpc('mark_announcement_report_generated', { p_announcement_id: id })
  if (error) throw error
  return data as {
    retention_started?: boolean
    report_generated_at?: string
    delete_after?: string
    reason?: string
  } | null
}

export async function deleteArchivedAnnouncementNow(id: number) {
  const { error } = await supabase.rpc('delete_archived_announcement_now', { p_announcement_id: id })
  if (error) throw error
}

export async function loadAnnouncementManagement(): Promise<AnnouncementManagementRow[]> {
  const { data, error } = await supabase.rpc('announcement_management')
  if (error) throw error
  return (data ?? []) as AnnouncementManagementRow[]
}

export async function loadAnnouncementFollowup(id: number): Promise<AnnouncementFollowupRecipient[]> {
  const { data, error } = await supabase.rpc('announcement_followup', { p_announcement_id: id })
  if (error) throw error
  const payload = data as { recipients?: AnnouncementFollowupRecipient[] } | null
  return payload?.recipients ?? []
}
